import {
  createQuestion,
  deleteQuestion,
  updateQuestion,
} from '@/services/api/question';
import { ApiErrorResponse } from '@/types/api/common';
import {
  CreateQuestionRequest,
  CreateQuestionResponse,
  DeleteQuestionRequest,
  DeleteQuestionResponse,
  UpdateQuestionRequest,
  UpdateQuestionResponse,
} from '@/types/api/question';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface UpdateQuestionContext {
  previousQuestions: any;
}

interface DeleteQuestionContext {
  previousQuestions: any;
}

export const useCreateQuestion = () => {
  const queryClient = useQueryClient();

  const createQuestionApi = useMutation<
    CreateQuestionResponse,
    ApiErrorResponse,
    CreateQuestionRequest
  >({
    mutationFn: (payload) => createQuestion(payload),
  });

  const updateQuestionApi = useMutation<
    UpdateQuestionResponse,
    ApiErrorResponse,
    UpdateQuestionRequest,
    UpdateQuestionContext
  >({
    mutationFn: (payload) => updateQuestion(payload),
    onMutate: async (updatedQuestion) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['infinite-questions-listing'],
      });

      // Snapshot the previous value
      const previousQuestions = queryClient.getQueryData([
        'infinite-questions-listing',
      ]);

      // Optimistically update the questions list
      queryClient.setQueryData(['infinite-questions-listing'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: page.data.map((question: any) => {
              if (question.id === updatedQuestion.id) {
                return {
                  ...question,
                  ...updatedQuestion,
                };
              }
              return question;
            }),
          })),
        };
      });

      // Return a context object with the snapshotted value
      return { previousQuestions };
    },
    onError: (err, newQuestion, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousQuestions) {
        queryClient.setQueryData(
          ['infinite-questions-listing'],
          context.previousQuestions
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure cache is in sync
      queryClient.invalidateQueries({
        queryKey: ['infinite-questions-listing'],
      });
    },
  });

  const deleteQuestionApi = useMutation<
    DeleteQuestionResponse,
    ApiErrorResponse,
    DeleteQuestionRequest,
    DeleteQuestionContext
  >({
    mutationFn: (payload) => deleteQuestion(payload),
    onMutate: async ({ id }) => {
      // Cancel any outgoing refetches for both questions and stories
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ['infinite-questions-listing'] }),
        queryClient.cancelQueries({ queryKey: ['infinite-stories'] }),
      ]);

      // Snapshot the previous values
      const previousQuestions = queryClient.getQueryData([
        'infinite-questions-listing',
      ]);

      // Optimistically update the questions list
      queryClient.setQueryData(['infinite-questions-listing'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: page.data.filter((question: any) => question.id !== id),
            totalCount: page.totalCount - 1,
          })),
        };
      });

      // Return a context object with the snapshotted value
      return { previousQuestions };
    },
    onError: (err, newQuestion, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousQuestions) {
        queryClient.setQueryData(
          ['infinite-questions-listing'],
          context.previousQuestions
        );
      }
    },
    onSettled: () => {
      // Invalidate both questions and stories queries to ensure cache is in sync
      queryClient.invalidateQueries({
        queryKey: ['infinite-questions-listing'],
      });
      queryClient.invalidateQueries({
        queryKey: ['infinite-stories'],
      });
    },
  });

  return {
    createQuestionApi,
    updateQuestionApi,
    deleteQuestionApi,
  };
};
